import { useState } from "react";
import { useNavigate } from "react-router";

import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  useLibraryPlacementRegistryQuery,
  type ClosingConversationAsset,
  type PlacementRegistry,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import type { DraftPart } from "@/lib/assetContent";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import {
  CLOSING_PROMPT_ROLE,
  ENGINE_KEYWORD_DONE,
  addStep,
  closingAnchorLanguage,
  closingStepsDocuments,
  moveStep,
  nextStepId,
  removeStep,
  setStepInstruction,
} from "@/lib/closingConversation";
import { findNamedRow, namedRows, updateNamedDocument } from "@/lib/namedParts";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import {
  LifecycleDialog,
  type LifecycleMode,
} from "@/ui/library/LifecycleDialog";
import { LoadingState, PartialDataNotice, UnavailableState } from "@/ui/states";

import { ClosingStepRows } from "./ClosingStepRows";
import { NamedAssetMeta } from "./NamedAssetChrome";
import {
  NamedDocumentActions,
  NamedDocumentEditor,
  isAuthorableDocumentSet,
} from "./NamedDocumentEditor";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewClosingConversationDialog } from "./NewClosingConversationDialog";
import { useNamedDraft } from "./useNamedDraft";

/**
 * One closing conversation, open for authoring (#867).
 *
 * ## WHAT IT REPLACES, AND WHY
 *
 * `AssetEditor` + `PartsEditor`, which is what a `closing_conversation` reached
 * from #866 until now: a free-text "Part key" input above a textarea holding the
 * raw steps document as JSON. That is the arbitrary file editor the ratified S7
 * screen contract forbids, and #866's own note says so in as many words — "#867
 * owns the Lovable-shaped step editor and the Project-side entry, exactly as
 * #643 later replaced this dialog for the Pathway family".
 *
 * ## CLOSING IS AN EXPLICIT ADDITION TO LOVABLE
 *
 * There is no Lovable closing screen; the S7 screen contract lists this row as
 * "Closing (new)" and names the two references whose PATTERNS it must reuse —
 * the [Pathway library](https://noproductcontent.lovable.app/library/pathways)
 * row/editor patterns and the [Project Pathways
 * rail](https://noproductcontent.lovable.app/projects/mod/config/pathways). So
 * this page is the named editor those screens establish — breadcrumb, name as
 * `h1`, the metadata strip with its Details disclosure, Back/Save in the header,
 * Discard beside the save status — with the ordered step rows of the [BG domain
 * editor](https://noproductcontent.lovable.app/library/bg-sets/domain/family_childhood)
 * under the prompt. Nothing here claims a reference screen that does not exist:
 * the page says on screen that it is an addition.
 *
 * ## WHAT THE PINNED ENGINE DOES WITH THIS CONTENT
 *
 * The closing agent runs AFTER every final track and before the session ends. It
 * asks once whether the patient wants to add anything, accepts one reply, and
 * completes when its generated answer carries the engine's completion token. So
 * a document with no instruction naming that token describes a turn with no
 * reachable end, and the service refuses it before the version becomes
 * immutable. This surface says so BEFORE Save rather than only after: the
 * service's refusal deliberately does not quote the token, and a clinician
 * authoring from scratch would otherwise have nothing to act on.
 *
 * ## EVERY OTHER PART SURVIVES A SAVE, AND THAT IS STRUCTURAL
 *
 * `useNamedDraft` publishes the WHOLE parts array; this surface edits the
 * `prompt` row's documents and the `steps` documents in place and passes
 * everything else through untouched, exactly as `SummaryPromptPage` does. The
 * concurrency rules are #33's, unchanged: the pin is taken at load, a background
 * refetch never re-seeds a dirty draft, a clean draft re-seeds forward only, and
 * a stale save recovers through the shared `ConflictRecovery`.
 */
export function ClosingConversationPage({
  asset,
}: {
  asset: ClosingConversationAsset;
}) {
  const navigate = useNavigate();
  const state = useNamedDraft(asset);
  const registry = useLibraryPlacementRegistryQuery();
  const retryRegistry = useRetryPausedQuery(queryKeys.libraryPlacementRegistry);
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);

  const breadcrumbs = [
    { label: "Clinical library", to: "/library" },
    { label: "Closing conversation", to: "/library/closing" },
  ];

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <LoadingState label="Loading closing conversation content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Closing conversation content"
          title="This closing conversation's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this closing conversation's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a closing conversation"
          description="A closing conversation is authored as a prompt document and an ordered step document, and this asset's stored content is a different shape."
        />
      </>
    );
  }

  const { editor } = state;
  const rows = namedRows(editor.parts, "closing_conversation");
  const prompt = findNamedRow(rows, CLOSING_PROMPT_ROLE);
  const documents = closingStepsDocuments(editor.parts);
  const authorable = prompt !== null && isAuthorableDocumentSet(prompt.documents);
  // Whether any stored steps document already names the completion token. Read
  // across ALL of them: the engine opens the conversation language's own
  // document, so a `he` document naming it does not rescue an `en` one.
  const incomplete = documents.filter(
    (document) => document.view !== null && !document.view.instructsCompletion,
  );

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // Direction-specific caller gate (#647): archiving is `library.write`,
  // restoring is the admin/operations `library.restore` the ratified contract
  // (§2.1) reserves — the same split the service enforces on the two routes.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";

  const registryUnread = registry.isPaused || registry.isError;
  const registryFailure = registry.isPaused
    ? registry.failureReason
    : registry.isError
      ? registry.error
      : null;
  const paths = closingPathTemplates(registry.data);
  const anchor = closingAnchorLanguage(registry.data);

  return (
    <>
      <PageHeader
        breadcrumbs={[...breadcrumbs, { label: asset.name }]}
        title={asset.name}
        description={
          <span className="text-muted-foreground">
            The message a session ends on. The pinned engine runs it after every
            final track, asks once for anything the patient wants to add, accepts
            one reply and completes.
          </span>
        }
        meta={
          <NamedAssetMeta
            asset={asset}
            version={editor.pinned}
            identity={`closing/${asset.id}`}
            details={
              <>
                <div className="break-all">
                  target: conclusion (server-owned; this family has no authored
                  placement)
                </div>
                <div className="break-all">
                  anchor language: {anchor ?? "not reported"}
                </div>
                {paths.length === 0 ? (
                  <div className="break-all">writes: not reported</div>
                ) : (
                  paths.map((path) => (
                    <div key={path} className="break-all">
                      writes: {path}
                    </div>
                  ))
                )}
              </>
            }
          />
        }
        actions={
          <>
            {/* OFFERED WHATEVER THE ROW'S PROVENANCE, and GATED ON
                `library.write` — the catalog's own control explains both at
                length. Base canon ships no closing conversation, so a copy
                gated on `capabilities.seeded` could never be reached; and the
                dialog this opens ends in `POST /library/assets`, so an ungated
                one lets a reader name a copy they cannot create. */}
            <Tooltip
              content={
                write.reason ??
                (asset.capabilities.seeded
                  ? "Part of the base clinical library. The original stays as it is — make an editable copy to create your own version."
                  : `Creates your own closing conversation starting from ${asset.name}. The original stays exactly as it is.`)
              }
            >
              <Button
                variant="outline"
                disabled={write.reason !== null}
                onClick={() => setCopying(true)}
              >
                Create editable copy
              </Button>
            </Tooltip>
            <Tooltip
              content={
                archiveRefusal ?? `${archiveLabel} this closing conversation`
              }
            >
              <Button
                variant="outline"
                disabled={archiveRefusal !== null}
                onClick={() => setLifecycle(archiveMode)}
              >
                {archiveLabel}
              </Button>
            </Tooltip>
            <NamedDocumentActions
              readOnly={editor.readOnly || !authorable}
              dirty={editor.dirty}
              saving={editor.saving}
              onBack={() => void navigate("/library/closing")}
              onSave={() => editor.save()}
            />
          </>
        }
      />

      <div className="space-y-4">
        {/* SAID ON THE SCREEN, not only in a pull request. The S7 screen
            contract requires every UI leaf to link its exact reference, and this
            one has none: Closing is an explicit ADDITION, built from the Pathway
            library's row/editor patterns. Claiming a reference screen that does
            not exist is the failure the contract's evidence rule names. */}
        <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          Closing conversation is a new screen, not a copy of an existing
          prototype one. It reuses the shared clinical library&apos;s named
          prompt and ordered step editors; the closing agent itself is never a
          diagnostic pathway and takes no part in Always-assessed or Dynamic
          selection.
        </p>

        <PartialDataNotice
          visible={registryUnread}
          description="The Editor Service did not report this runtime's placement registry, so the exact pack files this closing conversation writes are not shown under Details. Its content, and everything else on this screen, is unaffected."
          {...(registryUnread && !isTerminalQueryError(registryFailure)
            ? {
                action: (
                  <Button variant="outline" onClick={retryRegistry}>
                    Try again
                  </Button>
                ),
              }
            : {})}
        />

        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        {prompt === null ? (
          // A closing conversation the service requires a `prompt` document of,
          // that does not carry one. Stated rather than rendered as an empty
          // editor: an empty box would invite a clinician to write into a
          // document this version does not have, and Save would refuse.
          <UnavailableState
            title="This version carries no closing prompt"
            description="A closing conversation is authored as one prompt document per language, and this stored version has none. Its other content is unchanged and nothing on this screen alters it."
          />
        ) : (
          <section aria-label="Closing prompt" className="space-y-2">
            <h2 className="text-sm font-medium text-foreground">
              Closing prompt
            </h2>
            <NamedDocumentEditor
              documents={prompt.documents}
              readOnly={editor.readOnly}
              readOnlyReason={editor.readOnlyReason}
              problems={editor.problems}
              onChange={(documentId, text) =>
                editor.change(updateNamedDocument(editor.parts, documentId, text))
              }
            />
          </section>
        )}

        <section aria-label="Closing steps" className="space-y-3">
          <div className="space-y-1">
            <h2 className="text-sm font-medium text-foreground">
              Closing steps
            </h2>
            <p className="text-sm text-muted-foreground">
              The instructions the closing turn is driven by, in the order they
              are written. Each step&apos;s id is the identity the runtime
              addresses it by — it stays with the step when the order changes,
              and is never its position.
            </p>
          </div>

          {incomplete.length > 0 ? (
            // BEFORE Save rather than only after it. The service refuses a
            // document whose steps never instruct the completion token, and its
            // refusal deliberately does not quote the token: a clinician
            // authoring from scratch would meet an accurate message they could
            // not act on. This is the missing half.
            <Notice tone="warning">
              No step here tells the closing agent to emit{" "}
              <code dir="ltr" className="font-mono">
                {ENGINE_KEYWORD_DONE}
              </code>
              . Without it the closing turn has no reachable end — the runtime
              keeps the conversation open for every reply — and the Editor
              Service refuses to publish this version.
            </Notice>
          ) : null}

          {documents.length === 0 ? (
            <UnavailableState
              title="This version carries no step document"
              description="A closing conversation is authored as one ordered step document per language, and this stored version has none. Its other content is unchanged and nothing on this screen alters it."
            />
          ) : (
            documents.map((document) => (
              <ClosingStepsDocumentEditor
                key={document.documentId}
                document={document}
                showLanguage={documents.length > 1}
                readOnly={editor.readOnly}
                readOnlyReason={editor.readOnlyReason}
                onChange={editor.change}
                parts={editor.parts}
              />
            ))
          )}
        </section>

        {editor.readOnly ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <SaveStatusLine
              status={
                editor.saving
                  ? "saving"
                  : editor.failure !== null
                    ? "error"
                    : editor.dirty
                      ? "dirty"
                      : editor.lastSavedAt
                        ? "saved"
                        : "clean"
              }
              lastSavedAt={editor.lastSavedAt}
            />
            {/* CANCELLING AN UNSAVED EDIT, on this screen rather than only as a
                navigation side effect. Save is NOT repeated here: it is in the
                page header, where every other named editor renders it, and one
                screen with two Save buttons is two answers to "did I save?". */}
            <Button
              variant="outline"
              disabled={!editor.dirty || editor.saving}
              onClick={editor.discard}
            >
              Discard
            </Button>
          </div>
        )}
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {copying ? (
        <NewClosingConversationDialog
          copyFrom={asset}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </>
  );
}

/**
 * One stored steps document's rows, wired to the draft.
 *
 * ONE EDITOR PER STORED DOCUMENT, never one per role, and that is
 * `NamedDocumentEditor`'s own rule applied to the structured half: part keys are
 * language-prefixed, and the pinned engine opens the conversation language's own
 * closing document (falling back to the anchor). `he/steps` and `en/steps`
 * therefore drive different sessions and are authored independently — a single
 * merged list would have to pick one and silently hide the other.
 */
function ClosingStepsDocumentEditor({
  document,
  showLanguage,
  readOnly,
  readOnlyReason,
  parts,
  onChange,
}: {
  document: ReturnType<typeof closingStepsDocuments>[number];
  showLanguage: boolean;
  readOnly: boolean;
  readOnlyReason: string | null;
  /** The whole draft, so every edit below replaces exactly one document in it. */
  parts: DraftPart[];
  onChange: (parts: DraftPart[]) => void;
}) {
  const label = showLanguage
    ? document.language === null
      ? "Steps, every language"
      : `Steps, language: ${document.language}`
    : "Closing steps, in the order the conversation runs them";

  if (document.view === null) {
    // A STORED document this editor cannot read. Versions are immutable and
    // re-validated on read, so a buffer that is not `{steps: [...]}` is content
    // the service holds and this surface must not present as an empty list —
    // an empty list plus an Add control would invite a clinician to author over
    // a document they cannot see.
    return (
      <UnavailableState
        title={
          showLanguage && document.language !== null
            ? `The ${document.language} step document could not be read`
            : "The step document could not be read"
        }
        description="Its stored content is not the ordered step document this editor renders, so its steps cannot be listed or changed here. The document itself is unchanged and still part of this asset."
      />
    );
  }

  const view = document.view;

  return (
    <div className="space-y-2">
      {showLanguage ? (
        <p className="text-xs font-medium text-muted-foreground">
          {document.language === null
            ? "Every language"
            : `Language: ${document.language}`}
        </p>
      ) : null}
      <ClosingStepRows
        steps={view.steps}
        language={document.language}
        readOnly={readOnly}
        readOnlyReason={readOnlyReason}
        listLabel={label}
        onChangeInstruction={(index, value) =>
          onChange(setStepInstruction(parts, document.documentId, index, value))
        }
        onMove={(index, to) =>
          onChange(moveStep(parts, document.documentId, index, to))
        }
        onRemove={(index) =>
          onChange(removeStep(parts, document.documentId, index))
        }
        add={{
          label: "Add step",
          refusal: readOnlyReason,
          onAdd: () =>
            onChange(
              addStep(parts, document.documentId, nextStepId(view), ""),
            ),
        }}
      />
    </div>
  );
}

/**
 * The exact pack files this family writes, as the SERVER's registry states them.
 *
 * Never a path this application spells: the family's location is server-owned
 * (`placement.kind === "fixed"`), and a directory shown here is a claim about
 * the file the freeze writes.
 */
function closingPathTemplates(registry: PlacementRegistry | undefined): string[] {
  const family = registry?.families.find(
    (entry) => entry.assetType === "closing_conversation",
  );
  const placement = family?.placement;
  if (!placement || placement.kind !== "fixed") {
    return [];
  }
  // `parts` is optional on the wire for the coordinate forms that carry theirs
  // per form, so a fixed family that reported none is "not reported" rather than
  // an empty list of files this asset writes.
  return (placement.parts ?? []).map((part) => part.pathTemplate);
}

